import { resolveAnchor, sidebarAnchor, type AnchorSpec } from '../engine/anchors';
import { closeFlowChrome } from './closeChrome';
import {
  chooseFirstOption,
  clickWhenReady,
  ensureChecked,
  findVisible,
  isChecked,
  isChosen,
  isFilled,
  waitUntil,
} from './formFill';
import type { TaskFlow } from './types';

// Campaigns and Templates are two tabs of one route now, so the tab marker in
// the query is what tells them apart, not the path.
const MARKETING_PATH = '/home/campaigns';
const onMarketingPath = (): boolean => window.location.pathname.startsWith(MARKETING_PATH);
const marketingTab = (): 'campaigns' | 'templates' =>
  new URLSearchParams(window.location.search).get('tab') === 'templates' ? 'templates' : 'campaigns';

const inDialog = (selector: string): string =>
  `[role="dialog"] ${selector}, [role="alertdialog"] ${selector}`;

const DIALOG = '[role="dialog"], [role="alertdialog"]';

const buttonLabelled = (label: string, within: string): AnchorSpec => ({
  structural: { within, match: 'button', labels: [label] },
});

const TAB_STRIP = '[data-testid="automations-network-tabs"]';

const tab = (label: string): AnchorSpec => buttonLabelled(label, TAB_STRIP);

const CAMPAIGNS_TAB = tab('Campaigns');
const TEMPLATES_TAB = tab('Templates');

const SIDEBAR_ROW = sidebarAnchor(['Campaigns', 'New Campaigns']);

const NEW_CAMPAIGN = buttonLabelled('New Campaign', 'body');
const NEW_TEMPLATE = buttonLabelled('New Template', 'body');

const onCampaignsTab = () =>
  onMarketingPath() && marketingTab() === 'campaigns' && Boolean(resolveAnchor(NEW_CAMPAIGN));
const onTemplatesTab = () =>
  onMarketingPath() && marketingTab() === 'templates' && Boolean(resolveAnchor(NEW_TEMPLATE));

const AI_OPEN = inDialog('[data-testid="template-generate-ai"]');
const AI_HIDE = inDialog('[data-testid="template-preview-rail-toggle"]');
const AI_PROMPT = inDialog('#ai-prompt');
const aiPanelOpen = (): boolean => Boolean(findVisible(AI_PROMPT));

const TEMPLATE_NAME = inDialog('input[placeholder="my_template_name"]');

const basicField = (infoButtonLabel: string): AnchorSpec => ({
  structural: {
    within: DIALOG,
    match: 'div.min-w-0.flex-col',
    containsSelector: `[aria-label="${infoButtonLabel}"]`,
  },
});

const TEMPLATE_CATEGORY = basicField('What each category means');
const TEMPLATE_LAYOUT = basicField('What each layout means');
const TEMPLATE_LANGUAGE = basicField('About template language');

const headerTypeRadio = (value: string): AnchorSpec => ({
  selector: inDialog(`#header-type-${value}`),
});
const HEADER_TEXT_CHIP = headerTypeRadio('TEXT');
const HEADER_CHIPS: AnchorSpec = {
  group: [headerTypeRadio('NONE'), headerTypeRadio('TEXT')],
};

const HEADER_TEXT = inDialog('input[placeholder="Enter header text"]');
const textHeaderOn = (): boolean => Boolean(findVisible(HEADER_TEXT));

const enableTextHeader = async (): Promise<void> => {
  if (textHeaderOn()) return;
  await clickWhenReady(HEADER_TEXT_CHIP);
  await waitUntil(textHeaderOn, 2000);
};

const TEMPLATE_BODY = inDialog('#body-text');

const variableSample = (position: number): string =>
  inDialog(`input[placeholder="Sample text for {{${position}}}"]`);

const SAMPLES_PANEL: AnchorSpec = {
  structural: {
    within: DIALOG,
    match: 'div.bg-gray-50.rounded-lg',
    containsSelector: 'input[placeholder^="Sample text for"]',
  },
};

const sampleBoxes = (): HTMLInputElement[] =>
  Array.from(
    document.querySelectorAll<HTMLInputElement>(inDialog('input[placeholder^="Sample text for"]')),
  );

const allSamplesFilled = (): boolean => sampleBoxes().every((box) => box.value.trim() !== '');

const TEMPLATE_FOOTER = inDialog('#footer-text');

const ADD_BUTTON = buttonLabelled('Add Button', DIALOG);
const QUICK_REPLY_TEXT = inDialog('input[placeholder="Quick reply text"]');
const quickReplyRowAdded = (): boolean => Boolean(findVisible(QUICK_REPLY_TEXT));

const TEMPLATE_SUBMIT = buttonLabelled('Create Template', DIALOG);

const templateFormVisible = () => Boolean(findVisible(TEMPLATE_NAME));
const templateDrawerOpen = () => templateFormVisible() || aiPanelOpen();

const CAMPAIGN_NAME = inDialog('input[placeholder="Enter campaign name"]');
const campaignFormOpen = () => Boolean(findVisible(CAMPAIGN_NAME));

const TEMPLATE_TRIGGER = '[data-testid="campaign-template-trigger"]';
const TEMPLATE_PLACEHOLDER = 'Select template';
const TEMPLATE_SEARCH = '[data-testid="campaign-template-search"]';
const templatePickerOpen = () => Boolean(findVisible(TEMPLATE_SEARCH));

const TEMPLATE_OPTION = '[data-radix-popper-content-wrapper] .overflow-y-auto > button';

const AUDIENCE_ALL = inDialog('#audience-tab-all');

const AUDIENCE_SOURCES: AnchorSpec = {
  group: [
    { selector: inDialog('#audience-tab-upload') },
    { selector: AUDIENCE_ALL },
    { selector: inDialog('#audience-tab-tags') },
    { selector: inDialog('#audience-tab-stages') },
  ],
};

const ALL_CONTACTS_ROW: AnchorSpec = {
  structural: { within: DIALOG, match: 'button', labels: ['All contacts'], labelWithin: 'span' },
};

const allContactsSelected = (): boolean =>
  resolveAnchor(ALL_CONTACTS_ROW)?.element.getAttribute('aria-pressed') === 'true';

const CAMPAIGN_SUBMIT = { selector: `button[type="submit"][form="campaign-form"]` };
const SEND_OPTIONS = { selector: inDialog('button[aria-label="Choose send option"]') };

const SEND_CONTROL: AnchorSpec = { group: [CAMPAIGN_SUBMIT, SEND_OPTIONS] };

const VARIABLES_GRID = inDialog('.grid.gap-3');

const variableValue = (position: number): string =>
  inDialog(`.grid.gap-3 > div:nth-of-type(${position}) input[placeholder="Enter value"]`);

const variableBoxes = (): HTMLInputElement[] =>
  Array.from(document.querySelectorAll<HTMLInputElement>(inDialog('input[placeholder="Enter value"]')));

const allVariablesFilled = (): boolean => variableBoxes().every((box) => box.value.trim() !== '');

const TRACK_CLICKS = inDialog('#track-clicks');

const PREVIEW = inDialog('[data-preview-chat]');

const COST_ROW: AnchorSpec = {
  structural: {
    within: DIALOG,
    match: 'div.rounded-xl.px-4.py-3',
    containsSelector: '[aria-label="Cost breakdown"]',
  },
};

const ADVANCED_TOGGLE: AnchorSpec = {
  structural: { within: DIALOG, match: 'button', labels: ['Show', 'Hide'] },
};

const RETRY_TOGGLE = inDialog('#retry-failed-enabled');
const OPTED_OUT_TOGGLE = inDialog('#include-opted-out-contacts');
const DESCRIPTION = inDialog('#description');
const PREDICTIONS = inDialog('#show-predictions');

const advancedOpen = (): boolean => Boolean(findVisible(OPTED_OUT_TOGGLE));

const openAdvanced = async (): Promise<void> => {
  if (advancedOpen()) return;
  await clickWhenReady(ADVANCED_TOGGLE);
  await waitUntil(advancedOpen, 2000);
};

const SEND_OPTIONS_POPOVER = '[data-radix-popper-content-wrapper] .w-80.rounded-xl';
const sendOptionsOpen = (): boolean => Boolean(findVisible(SEND_OPTIONS_POPOVER));

const closeSendOptions = async (): Promise<void> => {
  if (!sendOptionsOpen()) return;
  await clickWhenReady(SEND_OPTIONS);
  await waitUntil(() => !sendOptionsOpen(), 2000);
};

const two = (n: number) => String(n).padStart(2, '0');

let templateNameValue = '';
let campaignNameValue = '';

function stampRunNames(): void {
  const now = new Date();
  const stamp = `${two(now.getHours())}${two(now.getMinutes())}${two(now.getSeconds())}`;
  templateNameValue = `festive_offer_${stamp}`;
  campaignNameValue = `Festive offer ${two(now.getHours())}:${two(now.getMinutes())}:${two(
    now.getSeconds(),
  )}`;
}

stampRunNames();

const CAMPAIGN_DESCRIPTION_VALUE =
  'Festive sale push to every saved contact. Internal note — nobody outside the team sees this.';

const TEMPLATE_HEADER_VALUE = 'Festive Sale is live 🎉';

const TEMPLATE_BODY_VALUE =
  'Hi {{1}}, our festive sale is now live — {{2}} off everything until Sunday. ' +
  'Reply SHOP and we will send you the collection.';

const TEMPLATE_SAMPLE_VALUES = ['Priya', '20 percent'];

const TEMPLATE_BUTTON_VALUE = 'Shop now';

export const NEW_CAMPAIGNS_MODULE_TASK: TaskFlow = {
  id: 'walk-new-campaigns',
  stepId: 'new-campaigns',
  cta: 'Try it: campaigns and templates',
  preparingLabel: 'Opening Campaigns…',
  title: 'New Campaigns — campaigns and templates',
  summary: 'One tab at a time: a campaign, then the wording behind it.',
  doneTitle: 'Campaign sent, template written',
  doneDescription:
    'You built a campaign on the Campaigns tab and wrote the kind of template it sends on the Templates tab — the two halves of this module and the dependency between them. Nothing left this browser: both are written to your demo data.',

  beforeStart: async () => {
    stampRunNames();
    if (onCampaignsTab() || onTemplatesTab()) return;
    await clickWhenReady(SIDEBAR_ROW);
    const arrived = await waitUntil(onCampaignsTab, 5000);
    if (!arrived) {
      console.warn(
        '[tour-guide] "walk-new-campaigns": never confirmed arrival on /home/campaigns ' +
          '(route and/or New Campaign button). The first step may resolve nothing.',
      );
    }
  },

  afterEnd: async () => {
    await closeFlowChrome(() => campaignFormOpen() || templateDrawerOpen());
  },

  steps: [

    {
      id: 'campaigns-tab',
      label: 'Campaigns',
      title: 'Campaigns — where a send is put together',
      description:
        'This is the redesigned Campaigns module, and everything in it sits under the two tabs here. This one, Campaigns, is the sends themselves — who got what, and how it performed. Templates, beside it, is the wording those sends use, each piece approved by Meta before it can go out. We will take one tab at a time, starting with a campaign built from wording this account already had approved.',
      anchor: CAMPAIGNS_TAB,
      side: 'bottom',
      spotlight: true,
      ready: onCampaignsTab,
      forceHighlight: true,
      onNext: async () => {
        if (onCampaignsTab()) return;
        await clickWhenReady(CAMPAIGNS_TAB);
        await waitUntil(onCampaignsTab, 5000);
      },
      satisfied: onCampaignsTab,
      nextLabel: 'Try it: build a campaign',
      skipTo: 'templates-tab',
    },

    {
      id: 'campaign-open',
      label: 'New campaign',
      title: 'Start a campaign',
      description:
        'Every card on this board is one broadcast, with its delivery numbers. A campaign sends one approved template to many people at once, so the form asks three things: what to send, who to send it to, and when.',
      anchor: NEW_CAMPAIGN,
      side: 'bottom',
      spotlight: true,
      ready: onCampaignsTab,
      prepare: async () => {},
      onNext: async () => {
        if (campaignFormOpen()) return;
        await clickWhenReady(NEW_CAMPAIGN);
      },
      satisfied: campaignFormOpen,
      rewind: async () => {
        await closeFlowChrome(campaignFormOpen, 'rewind');
      },
      nextLabel: 'Open the campaign form',
      waitingHint: 'Press the button to open the campaign form.',
    },

    {
      id: 'campaign-name',
      label: 'Name',
      title: 'Name the campaign',
      description:
        'Filled in for you. Unlike a template name this one is for your own reporting only — nobody outside your team sees it, and there are no formatting rules.',
      anchor: { selector: CAMPAIGN_NAME },
      side: 'right',
      spotlight: true,
      fill: [[CAMPAIGN_NAME, campaignNameValue]],
      satisfied: () => isFilled(CAMPAIGN_NAME),
      waitingHint: 'Give the campaign a name to carry on.',
      optional: false,
    },

    {
      id: 'campaign-template-open',
      label: 'Template',
      title: 'Choose the template',
      description:
        'Here is the dependency the Templates tab exists for: a campaign sends one approved template, so this list holds only APPROVED ones — never a pending submission, and never an authentication template, which Meta reserves for one-time codes. Every entry in it was written on the Templates tab, which we open once this campaign is away. Opening the picker.',
      anchor: { selector: TEMPLATE_TRIGGER },
      side: 'right',
      spotlight: true,
      forceHighlight: true,
      prepare: async () => {},
      onNext: async () => {
        if (templatePickerOpen()) return;
        await clickWhenReady({ selector: TEMPLATE_TRIGGER });
      },
      nextLabel: 'Open the template list',
      satisfied: () => templatePickerOpen() || isChosen(TEMPLATE_TRIGGER, TEMPLATE_PLACEHOLDER),
      waitingHint: 'Press Next to open the template list.',
    },

    {
      id: 'campaign-template-pick',
      label: 'Template',
      title: 'Pick an approved template',
      description:
        'Picked for you — the first approved template in the account. Change it from the same dropdown whenever you want; the preview on the right redraws to match, exactly as the customer will see it.',
      anchor: { selector: TEMPLATE_SEARCH },
      side: 'right',
      spotlight: true,
      ready: () => {
        const box = findVisible(TEMPLATE_SEARCH)?.getBoundingClientRect();
        return Boolean(box && box.top >= 0 && box.left >= 0);
      },
      prepare: async () => {},
      onNext: async () => {
        if (isChosen(TEMPLATE_TRIGGER, TEMPLATE_PLACEHOLDER)) {
          if (templatePickerOpen()) await clickWhenReady({ selector: TEMPLATE_TRIGGER });
          return;
        }
        await chooseFirstOption(
          { selector: TEMPLATE_TRIGGER },
          { optionSelector: TEMPLATE_OPTION },
        );
      },
      nextLabel: 'Pick the first approved one',
      satisfied: () => isChosen(TEMPLATE_TRIGGER, TEMPLATE_PLACEHOLDER),
      waitingHint: 'Press Next to pick an approved template.',
      optional: false,
    },

    {
      id: 'campaign-variables',
      label: 'Variables',
      title: 'Fill the blanks in the wording',
      description:
        'Filled in for you. Every {{1}} in the template is a blank the campaign has to supply, and each takes one of two things: a constant, which sends the same words to everybody, or a contact field, which is what makes one template read personally to a thousand people. Pick a contact field from the dropdown and the box beside it becomes the fallback used when that contact has nothing stored.',
      anchor: { selector: VARIABLES_GRID },
      side: 'right',
      spotlight: true,
      fill: [
        [variableValue(1), 'Priya'],
        [variableValue(2), 'NR-10482'],
        [variableValue(3), 'Friday'],
      ],
      satisfied: allVariablesFilled,
      waitingHint: 'Give every blank a value to carry on.',
    },

    {
      id: 'campaign-track-clicks',
      label: 'Link tracking',
      title: 'Count who clicks',
      description:
        'Left off, which is the drawer’s own default. Switch it on and every link in the message — inside a variable or behind a button — is rewritten as a short link before the send, which is the only way a WhatsApp campaign can report a click-through rate at all. The trade is that recipients see a shortened URL instead of your own domain.',
      anchor: { selector: TRACK_CLICKS },
      side: 'right',
      spotlight: true,
    },

    {
      id: 'campaign-preview',
      label: 'Preview',
      title: 'What the customer will see',
      description:
        'The phone on the right redraws every time the template or its blanks change, and it is the only place the header, the body, the footer and the buttons appear stacked the way WhatsApp will actually stack them. Worth reading before every send: wording that looks fine as a row in the template list can still be three lines too long here.',
      anchor: { selector: PREVIEW },
      side: 'left',
      spotlight: true,
    },

    {
      id: 'campaign-audience-source',
      label: 'Audience',
      title: 'Pick the audience source',
      description:
        'Recipients come from one of four places, and the row of choices here decides which chooser the form shows you. Switching it to Everyone.',
      anchor: AUDIENCE_SOURCES,
      side: 'right',
      spotlight: true,
      forceHighlight: true,
      prepare: async () => {
        if (isChecked(AUDIENCE_ALL)) return;
        ensureChecked(AUDIENCE_ALL);
        await waitUntil(() => isChecked(AUDIENCE_ALL), 1500);
      },
      advance: 'auto',
      satisfied: () => isChecked(AUDIENCE_ALL),
      waitingHint: 'Switching to Everyone…',
      optional: false,
    },

    {
      id: 'campaign-audience-all',
      label: 'Everyone',
      title: 'Choose who gets it',
      description:
        'Selected for you. Choosing All Contacts above already committed everyone as the audience — this row is the confirmation, not a second click. The other three sources are an uploaded spreadsheet, a set of tags, or a CRM stage, and any of those is the swap you would make on a real send that should not go to the whole list.',
      anchor: ALL_CONTACTS_ROW,
      side: 'right',
      spotlight: true,
      forceHighlight: true,
      prepare: async () => {
        if (!isChecked(AUDIENCE_ALL)) {
          ensureChecked(AUDIENCE_ALL);
          await waitUntil(() => Boolean(resolveAnchor(ALL_CONTACTS_ROW)), 2000);
        }
        if (allContactsSelected()) return;
        await clickWhenReady(ALL_CONTACTS_ROW);
        await waitUntil(allContactsSelected, 1500);
      },
      satisfied: () => allContactsSelected(),
      waitingHint: 'Choose some recipients to carry on.',
      optional: false,
    },

    {
      id: 'campaign-cost',
      label: 'Cost',
      title: 'What this send will cost',
      description:
        'Recipients multiplied by the per-conversation rate for this template’s CATEGORY — marketing costs more than utility, which is why the category you give a template decides the bill as much as the audience does. The info icon opens the breakdown. It moves as the audience does, so this is the number to read before pressing send rather than after.',
      anchor: COST_ROW,
      side: 'right',
      spotlight: true,
    },

    {
      id: 'campaign-advanced-open',
      label: 'Advanced',
      title: 'Open the advanced settings',
      description:
        'Everything a first campaign can safely ignore lives behind this toggle, collapsed so the form stays short: what to do about recipients the send fails to reach, whether people who opted out are messaged at all, and a note for your own team. We will open it and take the three in turn.',
      anchor: ADVANCED_TOGGLE,
      side: 'right',
      spotlight: true,
      onNext: openAdvanced,
      satisfied: advancedOpen,
      nextLabel: 'Open advanced settings',
      waitingHint: 'Press the button to open the advanced settings.',
    },

    {
      id: 'campaign-retry',
      label: 'Retries',
      title: 'Retry the ones that fail',
      description:
        'Left off. Sends fail for reasons that pass — a handset with no signal, a number briefly unreachable — so switching this on re-attempts only those recipients, up to twice, at least a day apart. Nobody the first pass reached is messaged again, which is what separates a retry from simply sending the campaign twice.',
      anchor: { selector: RETRY_TOGGLE },
      side: 'right',
      spotlight: true,
      prepare: openAdvanced,
    },

    {
      id: 'campaign-opted-out',
      label: 'Opted out',
      title: 'Leave the opt-outs alone',
      description:
        'Left off, and it should stay off for a marketing send. Anyone who replied STOP is skipped by default; turning this on messages them anyway, which Meta treats as a policy breach and can suspend the number for. The one legitimate use is a transactional or utility message they separately consented to — an order update, not an offer.',
      anchor: { selector: OPTED_OUT_TOGGLE },
      side: 'right',
      spotlight: true,
      prepare: openAdvanced,
    },

    {
      id: 'campaign-description',
      label: 'Note',
      title: 'Leave a note for your team',
      description:
        'Filled in for you, and optional. Nobody outside your team ever sees this — it is the line that explains, three months later, why a campaign called “Festive offer” went to everybody. The campaign NAME at the top is likewise internal; the only text a customer reads is the template.',
      anchor: { selector: DESCRIPTION },
      side: 'right',
      spotlight: true,
      prepare: openAdvanced,
      fill: [[DESCRIPTION, CAMPAIGN_DESCRIPTION_VALUE]],
      satisfied: () => isFilled(DESCRIPTION),
      waitingHint: 'Write a note, or move on — this one is optional.',
    },

    {
      id: 'campaign-predictions',
      label: 'Forecast',
      title: 'Ask for a forecast first',
      description:
        'Switched on for you. With a template and an audience both in place, this estimates how much of the send will land, be read, and be blocked, from how comparable wording has performed — a sanity check before you commit the spend, not a promise. It stays greyed out until both are chosen, which is why it comes last rather than beside the template.',
      anchor: { selector: PREDICTIONS },
      side: 'left',
      spotlight: true,
      forceHighlight: true,
      prepare: async () => {
        if (isChecked(PREDICTIONS)) return;
        ensureChecked(PREDICTIONS);
        await waitUntil(() => isChecked(PREDICTIONS), 1500);
      },
      satisfied: () => isChecked(PREDICTIONS),
      waitingHint: 'Turning the forecast on…',
    },

    {
      id: 'campaign-send-mode',
      label: 'Timing',
      title: 'Send now, or schedule it',
      description:
        'The button says what will happen: Send Now. The chevron beside it turns this into a scheduled send instead — pick a date, a time and a timezone, and the campaign sits until then, editable and cancellable the whole time. Leaving it on Send Now.',
      anchor: SEND_CONTROL,
      side: 'top',
      spotlight: true,
      forceHighlight: true,
    },

    {
      id: 'campaign-schedule-options',
      label: 'Schedule',
      title: 'Inside the send menu',
      description:
        'This is what the chevron opens. Send Now is ticked; choosing Schedule for later adds a date, a time and a timezone, and the campaign then sits in the list as SCHEDULED — editable and cancellable right up to the minute it goes out. We are leaving it on Send Now, and closing this again on the way past.',
      anchor: { selector: SEND_OPTIONS_POPOVER },
      side: 'left',
      spotlight: true,
      prepare: async () => {
        if (sendOptionsOpen()) return;
        await clickWhenReady(SEND_OPTIONS);
        await waitUntil(sendOptionsOpen, 2000);
      },
      onNext: closeSendOptions,
    },

    {
      id: 'campaign-submit',
      label: 'Send',
      title: 'Create and send',
      description:
        'Everything required is in place: a name, an approved template and an audience — which is why the button is no longer greyed out. Cancel beside it throws the whole draft away; this one creates the campaign, and because the mode is immediate, creating it also starts the send. Nothing leaves this browser: the campaign and its delivery numbers are written to your demo data, and it appears as the first card on the board behind this drawer.',
      anchor: CAMPAIGN_SUBMIT,
      side: 'top',
      spotlight: true,
      advance: 'auto',
      prepare: closeSendOptions,
      satisfied: () => !campaignFormOpen(),
      waitingHint: 'Waiting for you to press the send button…',
      optional: false,
    },

    {
      id: 'templates-tab',
      label: 'Templates',
      title: 'Templates — the wording behind every send',
      description:
        'The campaign you just built had to pick its wording from a list of approved templates, and this tab is where that list comes from. Meta requires a template to be approved BEFORE a campaign can use it, because a campaign is what starts the conversation — so this tab is the first stop for anything you have not sent before.',
      anchor: TEMPLATES_TAB,
      side: 'bottom',
      spotlight: true,
      ready: onCampaignsTab,
      prepare: async () => {},
      onNext: async () => {
        if (onTemplatesTab()) return;
        await clickWhenReady(TEMPLATES_TAB);
        await waitUntil(onTemplatesTab, 5000);
      },
      satisfied: onTemplatesTab,
      nextLabel: 'Try it: write a template',
      skipTo: 'end',
      waitingHint: 'Press the button to open the Templates tab.',
    },

    {
      id: 'template-open',
      label: 'New template',
      title: 'Write a template',
      description:
        'Every card here is one piece of wording, with the status Meta gave it. A template is a message with blanks in it, submitted once and then reusable for every send — which is why the campaign form could offer you a list to pick from rather than a text box to write in.',
      anchor: NEW_TEMPLATE,
      side: 'bottom',
      spotlight: true,
      ready: onTemplatesTab,
      prepare: async () => {},
      onNext: async () => {
        if (templateDrawerOpen()) return;
        await clickWhenReady(NEW_TEMPLATE);
      },
      satisfied: templateDrawerOpen,
      rewind: async () => {
        await closeFlowChrome(templateDrawerOpen, 'rewind');
      },
      nextLabel: 'Open the composer',
      waitingHint: 'Press the button to open the template composer.',
    },

    {
      id: 'template-ai-option',
      label: 'By hand',
      title: 'Draft it with AI, or write it yourself',
      description:
        'The composer opens on the empty form with a WhatsApp preview on the right. Press Nexa AI in the header to describe the message in a sentence — it drafts three versions in the right rail for you to pick from and then edit like any other. We are filling this one in by hand instead, so you can see what a template is actually made of — the button stays there for the next one.',
      anchor: { selector: AI_OPEN },
      side: 'left',
      spotlight: true,
      prepare: async () => {
        if (!aiPanelOpen()) return;
        await clickWhenReady(AI_HIDE);
        await waitUntil(templateFormVisible, 2000);
      },
      satisfied: templateFormVisible,
      waitingHint: 'Opening the form…',
    },

    {
      id: 'template-name',
      label: 'Name',
      title: 'Name the template',
      description:
        'Filled in for you. Meta is strict here: lowercase letters, numbers and underscores only, no spaces — the composer lowercases and swaps spaces for underscores as you type rather than refusing them. The name has to be unique in your account, which is why this one carries the time, and it is an internal identifier the customer never sees.',
      anchor: { selector: TEMPLATE_NAME },
      side: 'right',
      spotlight: true,
      fill: [[TEMPLATE_NAME, templateNameValue]],
      satisfied: () => isFilled(TEMPLATE_NAME),
      waitingHint: 'Give the template a name to carry on.',
      optional: false,
    },

    {
      id: 'template-category',
      label: 'Category',
      title: 'Say what kind of message this is',
      description:
        'Left on Marketing, which is what a festive offer is. These three are not a filing system — Meta prices them differently and holds them to different rules. Marketing is anything promotional and costs the most. Utility follows something the customer already did, an order or a booking, and costs less. Authentication is reserved for one-time codes and cannot be sent by a campaign at all. Choosing the wrong one is the commonest reason a template comes back rejected.',
      anchor: TEMPLATE_CATEGORY,
      side: 'bottom',
      spotlight: true,
    },

    {
      id: 'template-layout',
      label: 'Layout',
      title: 'One message, or a swipeable set',
      description:
        'Left on Standard: a single message with a header, a body, a footer and buttons — everything the rest of this form asks for. Carousel is two to ten swipeable cards instead, each with its own image, wording and buttons. Meta allows it for Marketing only, which is why this choice disappears the moment the category above changes, and why the form below it changes shape entirely when you pick it.',
      anchor: TEMPLATE_LAYOUT,
      side: 'bottom',
      spotlight: true,
    },

    {
      id: 'template-language',
      label: 'Language',
      title: 'The language you are writing in',
      description:
        'Left on English. A template is approved PER LANGUAGE, so the same wording in Hindi is a second submission carrying the same name — which is how one campaign can reach each contact in the language they read. Declare it honestly: the review reads your text against the language you claimed.',
      anchor: TEMPLATE_LANGUAGE,
      side: 'bottom',
      spotlight: true,
    },

    {
      id: 'template-header',
      label: 'Header',
      title: 'Add a header',
      description:
        'Optional, and switched on for you as a text header. One header per template, in one of five formats: a line of text, an image, a video, a document, or a location — and None, at the start of the row, switches it back off. The media formats need a real file uploaded before Meta will accept the template; text needs nothing but the line itself, which is the next step.',
      anchor: HEADER_CHIPS,
      side: 'right',
      spotlight: true,
      prepare: enableTextHeader,
      satisfied: textHeaderOn,
      waitingHint: 'Switching the text header on…',
    },

    {
      id: 'template-header-text',
      label: 'Header text',
      title: 'Write the header',
      description:
        'Filled in for you. Sixty characters on one line, rendered in bold above the message — a subject line rather than a sentence, and it may carry at most one blank. Turning the header on is what made this field REQUIRED: Meta rejects a template whose header is enabled and empty, and the composer keeps Create Template greyed out until it has something.',
      anchor: { selector: HEADER_TEXT },
      side: 'right',
      spotlight: true,
      prepare: enableTextHeader,
      fill: [[HEADER_TEXT, TEMPLATE_HEADER_VALUE]],
      satisfied: () => isFilled(HEADER_TEXT),
      waitingHint: 'Give the header a line to carry on.',
    },

    {
      id: 'template-body',
      label: 'Message',
      title: 'Write the message',
      description:
        'Filled in for you, and this is the part the customer reads. Curly braces make a blank — {{1}} — that each campaign fills in per recipient, which is what makes one approved template serve every send. Number them from one, in order, and never mix numbered blanks with named ones in the same body. Meta rejects a body that begins or ends with a blank, so keep words on both sides of every one.',
      anchor: { selector: TEMPLATE_BODY },
      side: 'right',
      spotlight: true,
      fill: [[TEMPLATE_BODY, TEMPLATE_BODY_VALUE]],
      satisfied: () => isFilled(TEMPLATE_BODY),
      waitingHint: 'Write a message to carry on.',
      optional: false,
    },

    {
      id: 'template-samples',
      label: 'Samples',
      title: 'Show Meta what the blanks hold',
      description:
        'Filled in for you, and this panel appeared the moment the body gained its blanks. Meta reviews the message with your samples substituted in, so an example is what turns two curly braces into something a human can judge. Keep them realistic and keep them fictional — the panel says so itself, because a real customer name or number in here is a privacy problem in someone else’s review queue.',
      anchor: SAMPLES_PANEL,
      side: 'right',
      spotlight: true,
      fill: [
        [variableSample(1), TEMPLATE_SAMPLE_VALUES[0]],
        [variableSample(2), TEMPLATE_SAMPLE_VALUES[1]],
      ],
      satisfied: allSamplesFilled,
      waitingHint: 'Give every blank a sample to carry on.',
    },

    {
      id: 'template-footer',
      label: 'Footer',
      title: 'The opt-out line',
      description:
        'Already filled in, and left exactly as it is: the composer defaults every marketing template to "Reply STOP to unsubscribe". Sixty characters, no blanks allowed, rendered small and grey under the message. Meta does not force an opt-out line — but a marketing template without one collects blocks, and blocks are what pull your number’s quality rating down.',
      anchor: { selector: TEMPLATE_FOOTER },
      side: 'right',
      spotlight: true,
    },

    {
      id: 'template-buttons',
      label: 'Buttons',
      title: 'Add a button',
      description:
        'Optional, and up to ten per template: quick replies, which send their own words back to you when tapped; up to two URL buttons; one phone button; and one that opens a WhatsApp Flow. We will add a quick reply — the tap that replaces "Reply SHOP" in the message with something the customer does not have to type.',
      anchor: ADD_BUTTON,
      side: 'right',
      spotlight: true,
      onNext: async () => {
        if (quickReplyRowAdded()) return;
        await clickWhenReady(ADD_BUTTON);
        await waitUntil(quickReplyRowAdded, 2000);
      },
      satisfied: quickReplyRowAdded,
      nextLabel: 'Add a quick reply',
      waitingHint: 'Press the button to add one.',
    },

    {
      id: 'template-button-text',
      label: 'Button text',
      title: 'Label the button',
      description:
        'Filled in for you, and required — an empty button row is what keeps Create Template greyed out, so adding a button and leaving it blank is worse than not adding one. Forty characters. On a quick reply the label is both what the customer taps AND what arrives back in your inbox, so make it something you can act on. The dropdown beside it turns this row into a URL, phone or Flow button, each of which asks for one more field.',
      anchor: { selector: QUICK_REPLY_TEXT },
      side: 'right',
      spotlight: true,
      fill: [[QUICK_REPLY_TEXT, TEMPLATE_BUTTON_VALUE]],
      satisfied: () => isFilled(QUICK_REPLY_TEXT),
      waitingHint: 'Give the button a label to carry on.',
    },

    {
      id: 'template-preview',
      label: 'Preview',
      title: 'Read it back as the customer will',
      description:
        'Everything you have entered, stacked the way WhatsApp stacks it: the bold header, the body with your samples standing in for the blanks, the grey footer, and the button under all of it. Worth reading before every submission — this is the arrangement Meta reviews, not the list of boxes on the left.',
      anchor: { selector: PREVIEW },
      side: 'left',
      spotlight: true,
    },

    {
      id: 'template-submit',
      label: 'Create',
      title: 'Submit it for approval',
      description:
        'Everything Meta requires is in place: a name, a category, a language, a body, and a line for the header and the button we switched on. Cancel beside it throws the draft away. Creating it SUBMITS it, which is why it lands in the list as PENDING rather than ready to use — real approval takes anywhere from minutes to a day, and only then would it appear in the campaign form’s picker, which lists approved templates only. Nothing leaves this browser: the template is written to your demo data.',
      anchor: TEMPLATE_SUBMIT,
      side: 'top',
      spotlight: true,
      advance: 'auto',
      satisfied: () => !templateDrawerOpen(),
      waitingHint: 'Press Create Template to submit it…',
      optional: false,
    },
  ],
};
